import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import OnboardingShell from "./_Shell";
import {
  getOrCreateOnboarding,
  saveOnboarding,
  type PlanId,
} from "../../lib/onboarding";
import { cardBorderClassName } from "../../lib/cardStyles";
import { TRIAL_DAYS } from "../../lib/pricing";

const API_BASE =
  import.meta.env.VITE_API_URL || "https://app.neurospherevoiceai.com/api";

const STRIPE_LINKS: Record<PlanId, string | undefined> = {
  starter: import.meta.env.VITE_STRIPE_LINK_STARTER,
  growth: import.meta.env.VITE_STRIPE_LINK_GROWTH,
  elite: import.meta.env.VITE_STRIPE_LINK_ELITE,
};

const STRIPE_LABELS: Record<PlanId, string | undefined> = {
  starter: import.meta.env.VITE_STRIPE_PRICE_LABEL_STARTER,
  growth: import.meta.env.VITE_STRIPE_PRICE_LABEL_GROWTH,
  elite: import.meta.env.VITE_STRIPE_PRICE_LABEL_ELITE,
};

const DEFAULT_PLAN_NAMES: Record<PlanId, string> = {
  starter: "Starter",
  growth: "Growth",
  elite: "Elite",
};

function planLabel(plan: PlanId): string {
  const fromEnv = (STRIPE_LABELS[plan] || "").trim();
  return fromEnv || DEFAULT_PLAN_NAMES[plan];
}

function stripeLinkForPlan(plan: PlanId): string | undefined {
  const url = (STRIPE_LINKS[plan] || "").trim();
  return url || undefined;
}

export default function OnboardingCheckout() {
  const navigate = useNavigate();
  const ob = getOrCreateOnboarding();

  const step2 = ob.step2 as Record<string, unknown> | undefined;
  const step3 = ob.step3 as Record<string, unknown> | undefined;

  const businessName = String(step2?.businessName ?? step2?.business_name ?? "");
  const aiName = String(step3?.aiName ?? step3?.ai_name ?? "");
  const greeting = String(step3?.greeting ?? "");
  const businessType = String(
    (ob as Record<string, unknown>).businessType ?? "other",
  );
  const email = String(step2?.email ?? "");
  const plan = ob.plan;
  const selectedPlanName = planLabel(plan);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accountExists, setAccountExists] = useState(false);

  useEffect(() => {
    if (!ob.step2 || !ob.step3) {
      navigate("/onboarding/1");
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function pay() {
    setError(null);
    setAccountExists(false);

    const link = stripeLinkForPlan(plan);
    if (!link) {
      setError("Payment is temporarily unavailable");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/customers/onboard`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mode: "pending",
          business_name: businessName,
          contact_name: String(step2?.fullName ?? ""),
          email,
          password: String(step2?.password ?? ""),
          phone: String(step2?.phone ?? ""),
          agent_name: aiName,
          greeting_template: greeting,
          business_type: businessType,
        }),
      });

      const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;

      if (res.status === 409) {
        setAccountExists(true);
        return;
      }

      if (!res.ok) {
        setError(String(data?.error ?? data?.message ?? `HTTP ${res.status}`));
        return;
      }

      const customerId = data.customer_id;
      if (customerId == null) {
        setError("Could not create your account. Please try again.");
        return;
      }

      saveOnboarding({
        ...ob,
        customerId: Number(customerId),
        clientReferenceId: String(customerId),
      });

      localStorage.setItem("customerId", String(customerId));
      if (email) {
        localStorage.setItem("userEmail", email);
      }

      const payUrl = new URL(link);
      payUrl.searchParams.set("client_reference_id", String(customerId));
      if (email) {
        payUrl.searchParams.set("prefilled_email", email);
      }
      window.location.assign(payUrl.toString());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <OnboardingShell
      step={3}
      title="Almost there!"
      subtitle={`Add a card to start your ${TRIAL_DAYS}-day free trial. You won't be charged today.`}
    >
      <div className="space-y-5">
        <div
          className={`rounded-xl p-5 ${cardBorderClassName}`}
          style={{
            background: "rgba(13, 13, 18, 0.7)",
          }}
        >
          <h3
            className="text-xs uppercase tracking-widest text-gray-500 mb-4"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Your AI setup summary
          </h3>

          <div className="space-y-3">
            <div>
              <p
                className="text-xs text-gray-500 mb-0.5"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Plan
              </p>
              <p
                className="text-white font-semibold"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {selectedPlanName}
              </p>
            </div>

            <div>
              <p
                className="text-xs text-gray-500 mb-0.5"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Business name
              </p>
              <p
                className="text-white font-semibold"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {businessName || "—"}
              </p>
            </div>

            <div>
              <p
                className="text-xs text-gray-500 mb-0.5"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                AI receptionist name
              </p>
              <p
                className="text-white font-semibold"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                {aiName || "—"}
              </p>
            </div>

            <div>
              <p
                className="text-xs text-gray-500 mb-0.5"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Opening greeting
              </p>
              <p
                className="text-gray-300 text-sm italic"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                "{greeting || "—"}"
              </p>
            </div>
          </div>
        </div>

        {accountExists && (
          <div
            className="rounded-xl p-4 text-sm"
            style={{
              background: "rgba(220, 38, 38, 0.12)",
              border: "1px solid rgba(248, 113, 113, 0.35)",
              color: "rgb(252, 165, 165)",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            An account with this email already exists —{" "}
            <Link to="/login" className="underline text-white">
              log in instead
            </Link>
            .
          </div>
        )}

        {error && (
          <div
            className="rounded-xl p-4 text-sm"
            style={{
              background: "rgba(220, 38, 38, 0.12)",
              border: "1px solid rgba(248, 113, 113, 0.35)",
              color: "rgb(252, 165, 165)",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate("/onboarding/3")}
            disabled={loading}
            className="w-full rounded-xl border border-white/15 px-4 py-3 text-sm font-semibold text-white/90 hover:border-white/30 disabled:opacity-40 cursor-pointer"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Back
          </button>

          <button
            type="button"
            onClick={pay}
            disabled={loading}
            className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white disabled:opacity-40 cursor-pointer flex items-center justify-center gap-2"
            style={{
              background: "linear-gradient(90deg, #8a2be2, #ff6a00)",
              fontFamily: "'Space Grotesk', sans-serif",
            }}
          >
            {loading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Opening secure checkout…
              </>
            ) : (
              "Continue to secure checkout"
            )}
          </button>
        </div>
      </div>
    </OnboardingShell>
  );
}
